import Price from '@components/Price';
import c from 'currency.js';
import { toNumber } from 'lodash';
import { memo, useMemo, useRef } from 'react';
import { SlideDown } from '@teskon/react-slidedown';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import useDateTime from '@hooks/use-datetime';
import { periodHandler } from '@utilities/helpers/common';
import { parse } from 'date-fns';
import {
  SubHeader as SubHeaderClass,
  Car as CarClass,
  Left,
  Right,
  Total,
  PriceWrapper,
  Text,
  Price as PriceClass,
  Bold,
  Inner,
  MembershipWrapper,
  LineItemWrapper,
  SubscriptionName,
  TextSmall,
} from '../../OrderSummary.module.css';

const OrderItemsList = ({
  className,
  memberships,
  animate,
  isPreOrder,
  checkoutPrice,
}) => {
  const { t } = useTranslation(['account', 'common', 'checkout']);
  const { isRemovingMembership, isSingle, isBusiness } = useSelector(
    (state) => state.checkout
  );
  const { locale } = useRouter();
  const { format: formatDate } = useDateTime();

  const didAnimateItems = useRef([]);
  const subtotal = useMemo(
    () =>
      memberships.reduce(
        // take all added memberships
        (totalMembershipsSum, { summary: { plans = [], discounts } }) => {
          // registration
          const totalReg = plans
            ?.filter(({ is_addon }) => !is_addon)
            .reduce((reg, { registration }) => +toNumber(registration), 0);
          // calculate total discount
          const totalDiscount = discounts
            ? discounts.reduce(
                (total, { discount: { amount } }) => total + toNumber(amount),
                0
              )
            : 0;
          // then take all plans
          const totalPlansSum = plans?.reduce(
            (totalPlans, plan) =>
              totalPlans + (!isSingle ? plan?.period?.price : plan?.price),
            0
          );
          // subtract discount from total registration
          const calcDiscount = totalReg - totalDiscount;
          // add together
          return totalMembershipsSum + totalPlansSum + calcDiscount;
        },
        0
      ),
    [isSingle, memberships]
  );

  let total = subtotal;
  total = Number.isFinite(total) ? total : 0;

  const orderItems = useMemo(() => {
    const newMemberships = memberships.reduce(
      (
        allOrderItems,
        { uid, summary: { plans = [], discounts = [] }, plate }
      ) => {
        const addons = plans?.filter(({ is_addon: isAddon }) => isAddon);

        const orderItem = plans
          ?.filter((item) => !item.is_addon)
          .map(
            ({
              period /*: { date = new Date(), price, description } */,
              registration,
            }) => ({
              uid,
              orderItemIndetifier: plate,
              name: plans.find(({ is_addon }) => !is_addon)?.name || 'Unknown',
              description: period?.description,
              date: period?.date,
              price: period?.price,
              addons: [
                ...addons,

                {
                  name: t('signUpFee', { ns: 'checkout' }),
                  period: { price: toNumber(registration) },
                },

                // discount
                ...(discounts
                  ? discounts.map(({ discount: { name, amount } }) => ({
                      name,
                      period: { price: toNumber(amount) * -1 },
                      type: 'discount',
                    }))
                  : []),
              ],
            })
          );

        return [...allOrderItems, ...orderItem];
      },
      []
    );

    if (!didAnimateItems.current.length) {
      didAnimateItems.current = [
        ...didAnimateItems.current,
        ...memberships.map(({ uid }) => uid),
      ];
    }

    setTimeout(() => {
      didAnimateItems.current = [
        ...didAnimateItems.current,
        ...memberships.map(({ uid }) => uid),
      ];
    }, 500);

    return newMemberships;
    // we dont need to add membershipIds to dependencyArray
    // since memberships is directly linked to it, and because
    // otherwise you'll face a weird bug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberships]);

  const totalPrice = isPreOrder
    ? checkoutPrice?.preorderTotal
    : checkoutPrice?.total;

  return (
    <div className={className}>
      {!isSingle && (
        <div className={SubHeaderClass}>
          {!isPreOrder
            ? t('summaryInfo', { ns: 'checkout' })
            : t('preOrderMembership', { ns: 'checkout' })}
        </div>
      )}
      {/**
       * Membership
       */}
      {orderItems.map(
        ({ uid, orderItemIndetifier, name, price, date, addons }) => (
          <div className={Inner} key={uid}>
            <SlideDown
              transitionOnAppear={
                animate && !didAnimateItems.current.includes(uid)
              }
              className={MembershipWrapper}
              closed={isRemovingMembership === uid}
            >
              <div className={CarClass}>
                <div className={LineItemWrapper}>
                  <div className={[SubscriptionName, Left].join(' ')}>
                    <span>
                      {isSingle
                        ? t('singleWash', { ns: 'common' })
                        : `${name} `}
                    </span>
                    <span>
                      {orderItemIndetifier && `- ${orderItemIndetifier}`}
                    </span>
                  </div>
                  {/* TODO: trash icon */}
                  {/* <div className={Right}>Slet</div> */}
                </div>
                {!isSingle && (
                  <div className={LineItemWrapper}>
                    <div className={Left}>
                      {t('inPeriod', {
                        ns: 'checkout',
                        date: periodHandler({
                          period: date,
                          formatDate,
                          parse,
                        }),
                      })}
                    </div>

                    <div className={Right}>
                      <Price
                        amount={toNumber(c(price, { fromCents: true }))}
                        showDecimals
                      />
                    </div>
                  </div>
                )}
                {/**
                 * Addons
                 */}
                {addons &&
                  addons.map(
                    ({
                      name: addonName,
                      type,
                      period: { price: addonPrice },
                    }) => (
                      <div className={LineItemWrapper} key={addonName}>
                        <div
                          className={classNames(Left, {
                            [Bold]: type === 'discount',
                          })}
                        >
                          {addonName}
                        </div>
                        <div
                          className={classNames(Right, {
                            [Bold]: type === 'discount',
                          })}
                        >
                          <Price
                            amount={toNumber(
                              c(addonPrice, { fromCents: true })
                            )}
                            showDecimals
                          />
                        </div>
                      </div>
                    )
                  )}
              </div>
            </SlideDown>
          </div>
        )
      )}
      {/**
       * Discount
       */}
      {!!checkoutPrice?.discount && !isPreOrder && (
        <div className={CarClass}>
          <div className={LineItemWrapper}>
            <div className={[SubscriptionName, Left].join(' ')}>
              {t('extras', { ns: 'checkout' })}
            </div>
          </div>
          <div className={LineItemWrapper}>
            <div className={[Left, Bold].join(' ')}>
              {t('washbackDiscount', {
                product: checkoutPrice?.washbackName,
                ns: 'checkout',
              })}
            </div>
            <div className={[Right, Bold].join(' ')}>
              <Price
                amount={
                  -toNumber(c(checkoutPrice?.discount, { fromCents: true }))
                }
                showDecimals
              />
            </div>
          </div>
        </div>
      )}
      {/**
       * Business
       */}
      {isBusiness && (
        <div className={CarClass}>
          <div className={LineItemWrapper}>
            <div className={Left}>
              <span>{t('totalPriceExVat', { ns: 'checkout' })}</span>
            </div>
            <div className={Right}>
              <Price
                amount={toNumber(c(total, { fromCents: true }))}
                showDecimals
              />
            </div>
          </div>
          <div className={LineItemWrapper}>
            <div className={Left}>
              <span>{t('vat', { ns: 'checkout' })}</span>
            </div>
            <div className={Right}>
              <Price
                amount={toNumber(
                  c(total, { fromCents: true }) * 1.25 -
                    toNumber(c(total, { fromCents: true }))
                )}
                showDecimals
              />
            </div>
          </div>
        </div>
      )}
      {/**
       * Totalprice
       *  */}
      <div className={Total}>
        <div className={PriceWrapper}>
          <div className={Text}>
            {isPreOrder ? (
              <div>
                <div>{`${t('payLater.0', { ns: 'common' })}`}</div>
                {locale === 'de' && (
                  <div className={TextSmall}>{`${t('payLater.1', {
                    ns: 'common',
                  })}`}</div>
                )}
              </div>
            ) : (
              t('paymentDue', { ns: 'checkout' })
            )}
          </div>

          <div className={PriceClass}>
            <Price
              amount={toNumber(c(totalPrice, { fromCents: true }))}
              showDecimals
            />
          </div>
        </div>
      </div>
      {/**
       * Credit
       */}
      {!!checkoutPrice?.credit && (
        <div className={CarClass}>
          <div className={LineItemWrapper}>
            <div className={[SubscriptionName, Left].join(' ')}>
              {t('receivables', { ns: 'common' })}
            </div>
          </div>
          <div className={LineItemWrapper}>
            <div
              className={classNames(Left, {
                [Bold]: checkoutPrice?.discount,
              })}
            >
              {t('creditInfo', { ns: 'checkout' })}
            </div>
            <div className={Right}>
              <Price
                amount={toNumber(c(checkoutPrice?.credit, { fromCents: true }))}
                showDecimals
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(
  OrderItemsList,
  (previousProps, nextProps) =>
    JSON.stringify(previousProps.memberships) ===
      JSON.stringify(nextProps.memberships) &&
    previousProps.isLoading === nextProps.isLoading
);
